import { appUrl, type PlatformAppId } from "../config/environment";
import type { ApplicationRoute, IconName, RoleCategory } from "../types/domain";

export type AuthorizationClientId = Exclude<PlatformAppId, "auth">;

const AUTHORIZATION_RETURN_ORIGINS: Record<AuthorizationClientId, readonly string[]> = {
  learners: ["https://learners.academy.chatoner.com"],
  parents: ["https://parent.academy.chatoner.com"],
  instructors: ["https://instructors.academy.chatoner.com"],
  proctors: ["https://proctors.academy.chatoner.com"],
  organizations: ["https://organizations.academy.chatoner.com"],
  institutions: ["https://institutions.academy.chatoner.com"],
};

export interface AuthorizationClientContext {
  clientId: AuthorizationClientId;
  label: string;
  shortLabel: string;
  description: string;
  route: ApplicationRoute;
  role: string;
  roleCategory: RoleCategory;
  applicationRoleIds: readonly string[];
  invitationRoleIds: readonly string[];
  icon: IconName;
}

export const AUTHORIZATION_CLIENTS: Record<AuthorizationClientId, AuthorizationClientContext> = {
  learners: {
    clientId: "learners",
    label: "Learner Platform",
    shortLabel: "Learner",
    description: "Programs, cohorts, learning activity and credentials.",
    route: "learner-self",
    role: "learner",
    roleCategory: "learner",
    applicationRoleIds: ["learner"],
    invitationRoleIds: ["team-learner"],
    icon: "graduation",
  },
  parents: {
    clientId: "parents",
    label: "Parent & Guardian Platform",
    shortLabel: "Parent & guardian",
    description: "Family consent, schedules, billing and learner support.",
    route: "parent-child",
    role: "parent",
    roleCategory: "family",
    applicationRoleIds: ["parent", "guardian"],
    invitationRoleIds: ["parent", "guardian"],
    icon: "child",
  },
  instructors: {
    clientId: "instructors",
    label: "Instructor Platform",
    shortLabel: "Instructor",
    description: "Teaching, mentoring, content and human assessment.",
    route: "teaching",
    role: "instructor",
    roleCategory: "teaching",
    applicationRoleIds: ["instructor", "mentor", "assessor"],
    invitationRoleIds: ["instructor-lead"],
    icon: "badge",
  },
  proctors: {
    clientId: "proctors",
    label: "Exam Proctor Platform",
    shortLabel: "Exam proctor",
    description: "Assigned assessments, candidate support and factual incidents.",
    route: "proctor",
    role: "exam-proctor",
    roleCategory: "assessment",
    applicationRoleIds: ["exam-proctor"],
    invitationRoleIds: ["integrity-reviewer", "moderator"],
    icon: "eye",
  },
  organizations: {
    clientId: "organizations",
    label: "Organization Platform",
    shortLabel: "Organization",
    description: "Teams, cohorts, governance, sponsorship and operations.",
    route: "organization",
    role: "organization-owner",
    roleCategory: "organization",
    applicationRoleIds: ["organization-owner"],
    invitationRoleIds: [
      "program-manager",
      "people-admin",
      "team-manager",
      "governance-lead",
      "finance-admin",
      "procurement-officer",
      "technical-admin",
      "organization-auditor",
    ],
    icon: "building",
  },
  institutions: {
    clientId: "institutions",
    label: "Institution Platform",
    shortLabel: "Education institution",
    description: "Academic delivery, records, safeguarding and administration.",
    route: "institution",
    role: "institution-owner",
    roleCategory: "institution",
    applicationRoleIds: ["institution-owner"],
    invitationRoleIds: [
      "registrar",
      "dean",
      "department-head",
      "quality-team",
      "assessment-board",
      "student-success",
      "research-ethics",
      "institution-finance",
      "library-resources",
      "institution-it",
    ],
    icon: "school",
  },
};

export interface AuthorizationRequest {
  clientId: AuthorizationClientId;
  returnTo: string;
  state: string;
  role: string;
}

function isClient(value: string | null): value is AuthorizationRequest["clientId"] {
  return Boolean(value && Object.prototype.hasOwnProperty.call(AUTHORIZATION_CLIENTS, value));
}

/**
 * The client identity is safe to use for narrowing visible routes even when a
 * callback is missing or invalid. Redirect authority is still granted only by
 * readAuthorizationRequest after the return origin and state are validated.
 */
export function readAuthorizationClientId(): AuthorizationClientId | null {
  if (typeof window === "undefined") return null;
  const clientId = new URLSearchParams(window.location.search).get("client_id");
  return isClient(clientId) ? clientId : null;
}

export function authorizationContextFor(clientId: AuthorizationClientId) {
  return AUTHORIZATION_CLIENTS[clientId];
}

/** Reads and validates the frontend-only authorization request. Production replaces this with OIDC. */
export function readAuthorizationRequest(): AuthorizationRequest | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const clientId = readAuthorizationClientId();
  const returnTo = params.get("return_to");
  const state = params.get("state");
  if (!clientId || !returnTo || !state) return null;

  try {
    const callback = new URL(returnTo);
    const allowedOrigins = new Set([
      new URL(appUrl(clientId)).origin,
      ...AUTHORIZATION_RETURN_ORIGINS[clientId],
    ]);
    if (!allowedOrigins.has(callback.origin) || callback.username || callback.password) return null;
    return {
      clientId,
      returnTo: callback.toString(),
      state,
      role: AUTHORIZATION_CLIENTS[clientId].role,
    };
  } catch {
    return null;
  }
}

/** Compatibility name retained for imported prototype references. */
export const readMockAuthorizationRequest = readAuthorizationRequest;
